import { DataTableViewOptions } from "@/components/DataTable/Table/DataTableViewOptions";
import PopOver from "@/components/PopOver";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ChangePersonDialog from "@/features/app-user/components/ChangePersonDialog";
import { usePersonActionLabels } from "@/features/app-user/hooks/usePersonActionLabels";
import {
  type AppUser,
  type Person,
  RULE_MATCHING,
} from "@/features/app-user/types";
import { defaultNS, useTranslation } from "@/lib/i18n";
import { genNameInitials } from "@/utils/name";
import type { ColumnDef, Row } from "@tanstack/react-table";
import { useState } from "react";
import type { ListSoftwareAlertsInactiveUserResponse } from "../_alerts/inactive-user/-query/type";
import type { ListSoftwareAlertsNoUserResponse } from "../_alerts/no-user/-query/type";
import type { ListSoftwareAlertsNoWorkerResponse } from "../_alerts/no-worker/-query/type";
import type { ListSoftwareAlertsViolatedAssignmentResponse } from "../_alerts/violated-assignment/-query/type";
import type { ListSoftwareAlertsViolatedRoleResponse } from "../_alerts/violated-role/-query/type";

type AlertRow =
  | ListSoftwareAlertsNoWorkerResponse["items"][number]
  | ListSoftwareAlertsNoUserResponse["items"][number]
  | ListSoftwareAlertsViolatedAssignmentResponse["items"][number]
  | ListSoftwareAlertsViolatedRoleResponse["items"][number]
  | ListSoftwareAlertsInactiveUserResponse["items"][number];

export function ActionsColumn<T extends AlertRow>(): ColumnDef<T> {
  return {
    accessorKey: "actions",
    size: 60,
    header: ({ table }) => (
      <div className="flex justify-end pr-2">
        <DataTableViewOptions table={table} />
      </div>
    ),
    cell: ({ row }) => <ActionsCell row={row} />,
  };
}

function ActionsCell<T extends AlertRow>({ row }: { row: Row<T> }) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.apps.integrations",
  });
  const rowData: T = row.original;
  const [open, setOpen] = useState(false);

  const fromPerson: Person | null = rowData.worker
    ? {
        id: rowData.worker.id,
        name: rowData.worker.name,
        nameInitials: genNameInitials(rowData.worker.name),
        email: rowData.worker.email ?? "",
        avatarUrl: rowData.worker.avatarUrl ?? "",
      }
    : null;

  const fromAppUser: AppUser | null = rowData.user
    ? {
        id: rowData.user.id,
        name: rowData.user.name,
        displayName: rowData.user.displayName ?? "",
        nameInitials: rowData.user.displayName
          ? genNameInitials(rowData.user.displayName)
          : genNameInitials(rowData.user.name),
        email: rowData.user.email ?? "",
        avatarUrl: rowData.user.avatarUrl ?? "",
      }
    : null;

  const { actionLabel } = usePersonActionLabels(fromPerson);

  // NOTE:NoUserRowの場合はメンバー変更等のアクションがないためnullを返す
  if (rowData.ruleMatching === RULE_MATCHING.NO_USER || !fromAppUser) {
    return null;
  }

  // この時点でrowDataはViolatedUserRowまたはNoWorkerUserRow
  return (
    rowData.user && (
      <div className="flex justify-end pr-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <PopOver
              options={[
                {
                  label: actionLabel,

                  disabled: !rowData.canChangePerson,
                  action: () => setOpen(true),
                },
                {
                  label: t("delete"),
                  highlight: true,
                  disabled: true,
                  action: () => {},
                },
              ]}
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[160px]" />
        </DropdownMenu>
        {rowData.appIntegration?.appIntegrationId && (
          <ChangePersonDialog
            open={open}
            onOpenChange={setOpen}
            appIntegrationId={rowData.appIntegration.appIntegrationId}
            appUser={fromAppUser}
            person={fromPerson}
          />
        )}
      </div>
    )
  );
}
